/**
 * Feature Flag Service -- Singleton
 *
 * Cache-first, non-blocking feature flag service.
 * Uses StoreManagementClientFactory (Coral typed client) for Gateway API calls.
 *
 * Cache state machine:
 *   EMPTY -> FETCHING -> FRESH -> STALE -> REFRESHING -> FRESH
 *
 * Cache key format: `${storeId ?? 'global'}:${flagName}`
 *
 * TTL: 20 minutes (aligned with Cognito token refresh ~25 min)
 */

import { GetFeatureFlagsInput } from '@amzn/coral_com-amazon-jwostoremanagementgateway'
import { getServiceClient } from '@src/api/client-factory/StoreManagementClientFactory'
import { FEATURE_FLAG_REGISTRY, ALL_FLAG_NAMES } from './featureFlagRegistry'
import { ICachedFlagEntry, IFeatureFlagState, IFeatureFlagResponse, CacheState } from './featureFlags.types'
import { logger } from './featureFlagLogger'
import { getLocalDevConfig } from '@src/config/localDevConfig'

/** Cache TTL: 20 minutes in milliseconds */
const CACHE_TTL_MS = 20 * 60 * 1000

/** Fetch timeout: 5 seconds */
const FETCH_TIMEOUT_MS = 5000

/** In-memory cache: key = `${storeId|global}:${flagName}` */
const cache = new Map<string, ICachedFlagEntry>()

/** Track in-flight fetches to deduplicate concurrent requests */
const inFlightFetches = new Map<string, Promise<void>>()

/**
 * Per-scope request sequence. A newer forced refresh owns cache writes and supersedes older responses.
 */
const latestRequestIds = new Map<string, number>()

/**
 * Cache/session epoch. Logout increments this value so pre-logout requests cannot restore old data.
 */
let cacheGeneration = 0

/** Subscribers notified on cache update */
type Listener = () => void
const listeners = new Set<Listener>()

// ---------------------------------------------------------------------------
// Cache helpers
// ---------------------------------------------------------------------------

function cacheKey (flagName: string, storeId: string | null): string {
    return `${storeId ?? 'global'}:${flagName}`
}

function isFresh (entry: ICachedFlagEntry): boolean {
    return (Date.now() - entry.fetchedAt) < CACHE_TTL_MS
}

function getCacheState (flagName: string, storeId: string | null): CacheState {
    const key = cacheKey(flagName, storeId)
    const entry = cache.get(key)
    if (!entry) {
        return inFlightFetches.has(fetchKey(storeId)) ? 'FETCHING' : 'EMPTY'
    }
    if (isFresh(entry)) return 'FRESH'
    return inFlightFetches.has(fetchKey(storeId)) ? 'REFRESHING' : 'STALE'
}

function fetchKey (storeId: string | null): string {
    return storeId ?? '__global__'
}

// ---------------------------------------------------------------------------
// Notification
// ---------------------------------------------------------------------------

function notifyListeners (): void {
    listeners.forEach((fn) => {
        try { fn() } catch { /* listener errors must not propagate */ }
    })
}

// ---------------------------------------------------------------------------
// Core fetch logic
// ---------------------------------------------------------------------------

async function fetchFlags (storeId: string | null, force: boolean = false): Promise<void> {
    const fKey = fetchKey(storeId)

    // Deduplicate: if already fetching for this store, join existing promise
    if (!force && inFlightFetches.has(fKey)) {
        logger.debug('Deduplicating fetch', { storeId })
        return inFlightFetches.get(fKey)
    }

    // Determine which flags to request
    const flagsToFetch = ALL_FLAG_NAMES.filter((name) => {
        const entry = FEATURE_FLAG_REGISTRY[name]
        if (storeId === null) {
            // Global fetch: only non-store-scoped flags
            return !entry.isStoreScoped
        }
        // Store fetch: only store-scoped flags
        return entry.isStoreScoped
    })

    if (flagsToFetch.length === 0) {
        logger.debug('No flags to fetch for this scope', { storeId })
        return
    }

    logger.info('Fetch started', { storeId, flagCount: flagsToFetch.length, flags: flagsToFetch })

    const generationAtStart = cacheGeneration
    const requestId = (latestRequestIds.get(fKey) ?? 0) + 1
    latestRequestIds.set(fKey, requestId)
    // Both checks are required: generation protects logout, while request ID protects refresh ordering.
    const isCurrentRequest = (): boolean =>
        cacheGeneration === generationAtStart && latestRequestIds.get(fKey) === requestId

    const fetchPromise = (async () => {
        try {
            const client = getServiceClient()
            const input = GetFeatureFlagsInput.clone({
                flagNames: flagsToFetch,
                storeId: storeId ?? undefined,
            })

            // Race against timeout (with cleanup to prevent timer leak)
            let timeoutId: ReturnType<typeof setTimeout> | undefined
            const result = await Promise.race([
                client.getFeatureFlags(input).toPromise(),
                new Promise<never>((_, reject) => {
                    timeoutId = setTimeout(() => reject(new Error('Fetch timeout')), FETCH_TIMEOUT_MS)
                }),
            ]).finally(() => {
                if (timeoutId !== undefined) clearTimeout(timeoutId)
            })

            // Parse response and populate cache. Discard stale writes after logout or a newer refresh.
            const flagMap: Record<string, IFeatureFlagResponse> = (result).flags ?? {}
            if (!isCurrentRequest()) {
                logger.debug('Discarding stale fetch result', { storeId })
                return
            }
            const fetchedAt = Date.now()

            for (const flagName of flagsToFetch) {
                const key = cacheKey(flagName, storeId)
                const flagData = flagMap[flagName]
                const responseOmittedFlag = flagData === undefined

                cache.set(key, {
                    enabled: flagData?.enabled ?? FEATURE_FLAG_REGISTRY[flagName].defaultEnabled,
                    attributes: flagData?.attributes ?? {},
                    fetchedAt,
                    storeId,
                    source: responseOmittedFlag ? 'default' : 'backend',
                    error: responseOmittedFlag
                        ? `Gateway response omitted requested feature flag: ${flagName}`
                        : null,
                })
            }

            logger.info('Fetch completed', {
                storeId,
                flagCount: flagsToFetch.length,
                enabledCount: Object.values(flagMap).filter((f) => f?.enabled).length,
            })

            notifyListeners()
        } catch (err: any) {
            // Fail-open: retain a usable flag value while exposing diagnostic state.
            const error = err?.message ?? String(err)
            logger.error('Fetch failed -- using defaults (fail-open)', {
                storeId,
                error,
                errorType: err?.constructor?.name ?? 'unknown',
                errorBody: err?.body ? JSON.stringify(err.body).slice(0, 200) : undefined,
                status: err?.status ?? err?.statusCode ?? undefined,
            })

            if (!isCurrentRequest()) {
                logger.debug('Discarding stale fetch failure', { storeId })
                return
            }
            const fetchedAt = Date.now()
            for (const flagName of flagsToFetch) {
                const key = cacheKey(flagName, storeId)
                // On failure: set defaults for empty keys, bump fetchedAt on stale keys (natural backoff)
                if (!cache.has(key)) {
                    cache.set(key, {
                        enabled: FEATURE_FLAG_REGISTRY[flagName].defaultEnabled,
                        attributes: {},
                        fetchedAt,
                        storeId,
                        source: 'default',
                        error,
                    })
                } else {
                    // Retain the last usable value while recording the refresh failure and backoff time.
                    const existing = cache.get(key)
                    if (existing) cache.set(key, { ...existing, fetchedAt, error })
                }
            }
            notifyListeners()
        } finally {
            if (inFlightFetches.get(fKey) === fetchPromise) { inFlightFetches.delete(fKey) }
        }
    })()

    inFlightFetches.set(fKey, fetchPromise)
    return fetchPromise
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const featureFlagService = {
    /**
     * Pre-fetch all global (non-store-scoped) flags.
     *
     * Cache-first + in-flight dedup. Returns the fetch promise so callers that need to
     * await warmup (e.g. the CrossFrame appConfigFeatureFlags listener) can; existing
     * fire-and-forget callers (FeatureFlagProvider, useLogout) simply ignore the return.
     */
    prefetchGlobal (): Promise<void> {
        logger.info('prefetchGlobal called -- fetching non-store-scoped flags')
        return fetchFlags(null).catch(() => { /* errors already logged */ })
    },

    /**
     * Pre-fetch all store-scoped flags for a specific store.
     * Call on store selection / store switch.
     */
    prefetchForStore (storeId: string): Promise<void> {
        logger.info('prefetchForStore called', { storeId })
        if (!storeId) {
            logger.warn('prefetchForStore called with empty storeId -- skipping')
            return Promise.resolve()
        }
        return fetchFlags(storeId)
    },

    /**
     * Get a feature flag's current state.
     *
     * Returns immediately from cache (sync path).
     * If cache is stale, triggers background refresh and returns stale value.
     * If cache is empty, returns registry default and triggers fetch.
     *
     * @throws Error if flagName is not registered
     */
    get (flagName: string, storeId?: string | null): IFeatureFlagState {
        // Validate flag is registered
        if (!(flagName in FEATURE_FLAG_REGISTRY)) {
            throw new Error(
                `[FeatureFlags] Flag "${flagName}" is NOT registered. ` +
                `Register it in featureFlagRegistry.ts. Registered: [${ALL_FLAG_NAMES.join(', ')}]`,
            )
        }

        // Local dev only: getLocalDevConfig() is empty off the local hostnames.
        const localOverride = getLocalDevConfig().featureFlagOverrides?.[flagName]
        if (typeof localOverride === 'boolean') {
            return { enabled: localOverride, attributes: {}, source: 'default', loading: false, error: null }
        }

        const entry = FEATURE_FLAG_REGISTRY[flagName]
        const effectiveStoreId = entry.isStoreScoped ? (storeId ?? null) : null
        if (entry.isStoreScoped && storeId == null) {
            logger.warn('Store-scoped flag read without storeId -- returning default', { flagName })
            return { enabled: entry.defaultEnabled, attributes: {}, source: 'default', loading: false, error: null }
        }
        const key = cacheKey(flagName, effectiveStoreId)
        const cached = cache.get(key)
        const state = getCacheState(flagName, effectiveStoreId)

        logger.debug('Flag read', { flagName, storeId: effectiveStoreId, state })

        // Trigger background refresh if stale
        if (state === 'STALE') {
            fetchFlags(effectiveStoreId).catch(() => {})
        }

        // Trigger initial fetch if empty
        if (state === 'EMPTY') {
            fetchFlags(effectiveStoreId).catch(() => {})
        }

        if (cached) {
            return {
                enabled: cached.enabled,
                attributes: cached.attributes,
                loading: false,
                source: cached.source,
                error: cached.error,
            }
        }

        // No cache -- return default
        logger.info('Flag read (no cache, returning default)', { flagName, storeId: effectiveStoreId, defaultEnabled: entry.defaultEnabled })
        return {
            enabled: entry.defaultEnabled,
            source: 'default',
            attributes: {},
            loading: state === 'FETCHING' || state === 'EMPTY',
            error: null,
        }
    },

    /**
     * Subscribe to cache updates. Returns unsubscribe function.
     */
    subscribe (listener: Listener): () => void {
        listeners.add(listener)
        return () => { listeners.delete(listener) }
    },

    /**
     * Clear all cached data. Useful for logout.
     */
    clearCache (): void {
        cacheGeneration++
        cache.clear()
        inFlightFetches.clear()
        latestRequestIds.clear()
        logger.info('Cache cleared and in-flight writes invalidated')
        notifyListeners()
    },

    /**
     * Force refresh all flags. Returns promise that resolves when complete.
     */
    async refresh (storeId?: string | null): Promise<void> {
        await fetchFlags(storeId ?? null, true)
    },

    /** @internal -- resets module state between unit tests */
    _reset (): void {
        cacheGeneration = 0
        cache.clear()
        inFlightFetches.clear()
        latestRequestIds.clear()
        listeners.clear()
    },
    /** @internal -- exposed for testing */
    _getCacheSize (): number { return cache.size },
    _getCache (): ReadonlyMap<string, ICachedFlagEntry> { return cache },
}
